/* =====================================================================
 * Theme — ChatGPT 스타일 라이트/다크 테마
 * ===================================================================== */

const shared = {
  fonts: {
    sans: "'Pretendard Variable', Pretendard, -apple-system, 'Segoe UI', Roboto, sans-serif",
    mono: "'JetBrains Mono', 'SF Mono', Menlo, monospace",
  },
  sizes: {
    sidebarWidth: '260px',
    maxMessageWidth: '768px',
    inputMaxHeight: '200px',
    borderRadius: '12px',
    borderRadiusSm: '8px',
    borderRadiusLg: '24px',
    borderRadiusXl: '28px',
  },
  shadows: {
    sm: '0 1px 2px rgba(0,0,0,0.06)',
    md: '0 4px 12px rgba(0,0,0,0.08)',
    lg: '0 8px 24px rgba(0,0,0,0.12)',
    input: '0 0 0 2px rgba(0, 0, 0, 0.1)',
  },
  breakpoints: {
    mobile: '768px',
    tablet: '1024px',
    desktop: '1280px',
  },
  transitions: {
    fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
    normal: '250ms cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '350ms cubic-bezier(0.4, 0, 0.2, 1)',
    spring: '400ms cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
  phaseColors: {
    planning: '#8b5cf6',
    execution: '#3b82f6',
    evaluation: '#f59e0b',
    refinement: '#10b981',
    final: '#10a37f',
  },
  /** 마크다운 렌더러 전용 타이포그래피·스페이싱 토큰 (PC 기본값, Pretendard 기준) */
  markdown: {
    /* ── 본문 ── */
    fontSize: '1rem',
    lineHeight: '1.65',
    /* ── 제목 ── */
    h1: '1.5rem',
    h2: '1.3rem',
    h3: '1.15rem',
    headingLineHeight: '1.3',
    headingWeight: '600',
    /* ── 코드 블록 ── */
    codeFontSize: '0.8125rem',
    codeLineHeight: '1.55',
    codeHeaderPad: '7px 12px',
    codePad: '12px 16px',
    codePadLn: '12px 0 12px 7px',
    /* ── 코드 헤더·뱃지·버튼 (공용) ── */
    headerFontSize: '0.75rem',
    badgeFontSize: '0.7rem',
    /* ── 인라인 코드 ── */
    inlineCodeFontSize: '0.875em',
    inlineCodePad: '2px 5px',
    /* ── 테이블 ── */
    tableFontSize: '0.8125rem',
    tableHeaderFontSize: '0.75rem',
    tableCellPad: '8px 14px',
    /* ── 리스트 ── */
    listPadLeft: '1.4em',
    /* ── 아이콘 ── */
    iconSize: '12',
    /* ── blockquote ── */
    bqPad: '0.4em 0.9em',
  },
  /** 모바일 해상도(<768px) 오버라이드 토큰 (Pretendard 기준) */
  markdownMobile: {
    fontSize: '0.875rem',
    lineHeight: '1.55',
    h1: '1.125rem',
    h2: '1.05rem',
    h3: '0.9375rem',
    codeFontSize: '0.6875rem',
    codeHeaderPad: '4px 7px',
    codePad: '7px 10px',
    codePadLn: '7px 0 7px 3px',
    headerFontSize: '0.625rem',
    badgeFontSize: '0.6rem',
    inlineCodeFontSize: '0.775em',
    inlineCodePad: '1px 3px',
    tableFontSize: '0.7rem',
    tableHeaderFontSize: '0.625rem',
    tableCellPad: '5px 7px',
    listPadLeft: '1em',
    iconSize: '10',
    bqPad: '0.25em 0.5em',
  },
  /** 사용자 말풍선 토큰 (PC 기본값) */
  bubble: {
    rowGap: '16px',
    rowPad: '20px 16px',
    userPad: '10px 20px',
    userFontSize: '1rem',
    userLineHeight: '1.65',
    userMaxWidth: '75%',
    assistantFontSize: '1rem',
    assistantLineHeight: '1.7',
    fileFontSize: '0.8125rem',
    fileSizeFontSize: '0.6875rem',
    fileIconSize: '36px',
    fileExtFontSize: '0.5rem',
    previewWidth: '180px',
    errorFontSize: '0.875rem',
    errorPad: '10px 14px',
    metaFontSize: '0.75rem',
    actionBtnPad: '6px',
    actionIconSize: '14',
  },
  /** 사용자 말풍선 모바일 오버라이드 */
  bubbleMobile: {
    rowGap: '12px',
    rowPad: '14px 12px',
    userPad: '8px 14px',
    userFontSize: '0.9375rem',
    userLineHeight: '1.6',
    assistantFontSize: '0.9375rem',
    assistantLineHeight: '1.65',
    fileFontSize: '0.75rem',
    fileSizeFontSize: '0.625rem',
    fileIconSize: '30px',
    fileExtFontSize: '0.4375rem',
    previewWidth: '140px',
    errorFontSize: '0.8125rem',
    errorPad: '8px 10px',
    metaFontSize: '0.6875rem',
    actionBtnPad: '5px',
    actionIconSize: '12',
  },
  /** 모달 전용 토큰 (PC 기본값) */
  modal: {
    titleFontSize: '1.125rem',
    sectionTitleFontSize: '0.8125rem',
    sectionDescFontSize: '0.75rem',
    bodyFontSize: '0.875rem',
    smallFontSize: '0.75rem',
    badgeFontSize: '0.6875rem',
    labelFontSize: '0.8125rem',
    nameFontSize: '0.875rem',
    descFontSize: '0.75rem',
  },
  /** 모달 모바일 오버라이드 */
  modalMobile: {
    titleFontSize: '1rem',
    sectionTitleFontSize: '0.75rem',
    sectionDescFontSize: '0.6875rem',
    bodyFontSize: '0.8125rem',
    smallFontSize: '0.6875rem',
    badgeFontSize: '0.625rem',
    labelFontSize: '0.75rem',
    nameFontSize: '0.8125rem',
    descFontSize: '0.6875rem',
  },
  /** 메시지 입력박스 토큰 (PC 기본값) */
  input: {
    wrapperPad: '0 16px 16px',
    rowPad: '8px 8px 8px 16px',
    fontSize: '1rem',
    lineHeight: '1.5',
    textPad: '6px 0',
    iconBtnSize: '36px',
    attachGap: '8px',
    attachPad: '12px 16px 4px',
    filePad: '10px 32px 10px 10px',
    fileIconSize: '42px',
    fileFontSize: '0.8125rem',
    fileSizeFontSize: '0.6875rem',
    fileExtFontSize: '0.5625rem',
    hintFontSize: '0.75rem',
    hintPad: '4px 0 0',
    uploadFontSize: '0.8125rem',
  },
  /** 메시지 입력박스 모바일 오버라이드 */
  inputMobile: {
    wrapperPad: '0 10px 10px',
    rowPad: '6px 6px 6px 12px',
    fontSize: '0.9375rem',
    lineHeight: '1.5',
    textPad: '5px 0',
    iconBtnSize: '32px',
    attachGap: '6px',
    attachPad: '10px 12px 4px',
    filePad: '8px 28px 8px 8px',
    fileIconSize: '36px',
    fileFontSize: '0.75rem',
    fileSizeFontSize: '0.625rem',
    fileExtFontSize: '0.5rem',
    hintFontSize: '0.6875rem',
    hintPad: '3px 0 0',
    uploadFontSize: '0.75rem',
  },
  /** DM(다이렉트 메시지) 전용 토큰 (PC 기본값) — 사이드바와 동일한 디자인 언어 */
  dm: {
    /* ── 에이전트 목록 ── */
    listWidth: '300px',
    listMinWidth: '260px',
    listMaxWidth: '340px',
    headerPad: '18px 16px',
    headerTitleFontSize: '0.8125rem',
    headerTitleWeight: '600',
    listPad: '0 8px',
    listItemPad: '8px 10px',
    listItemGap: '10px',
    listItemRadius: '8px',
    /* ── 알파벳 아바타 (팀 멤버 검색 아바타와 동일 28px) ── */
    avatarSize: '28px',
    avatarFontSize: '11px',
    avatarFontWeight: '700',
    avatarDotSize: '7px',
    avatarDotBorder: '2px',
    /* ── 목록 텍스트 ── */
    nameFontSize: '0.875rem',
    nameWeight: '500',
    previewFontSize: '0.8125rem',
    timeFontSize: '0.6875rem',
    badgeSize: '18px',
    badgeFontSize: '0.625rem',
    /* ── 채팅 스레드 ── */
    threadMaxWidth: '680px',
    threadHeaderPad: '14px 20px',
    threadHeaderAvatarSize: '32px',
    threadHeaderAvatarFontSize: '12px',
    threadHeaderNameFontSize: '0.875rem',
    threadHeaderDescFontSize: '0.75rem',
    messagesPad: '16px 20px',
    messagesGap: '14px',
    /* ── 말풍선 아바타 ── */
    msgAvatarSize: '28px',
    msgAvatarFontSize: '11px',
    /* ── 메시지 말풍선 ── */
    bubbleMaxWidth: '75%',
    bubblePad: '10px 14px',
    bubbleFontSize: '0.875rem',
    bubbleLineHeight: '1.5',
    bubbleRadius: '18px',
    bubbleRadiusTail: '4px',
    metaFontSize: '0.6875rem',
    /* ── 입력창 ── */
    inputPad: '12px 20px',
    inputInnerPad: '10px 14px',
    inputFontSize: '0.875rem',
    inputRadius: '24px',
    inputSendSize: '34px',
    /* ── 빈 상태 ── */
    emptyAvatarSize: '56px',
    emptyAvatarFontSize: '20px',
    emptyTitleFontSize: '1.125rem',
    emptyDescFontSize: '0.8125rem',
  },
  /** DM 모바일 오버라이드 */
  dmMobile: {
    listWidth: '100%',
    listMinWidth: '100%',
    listMaxWidth: '100%',
    headerPad: '10px 12px',
    headerTitleFontSize: '0.625rem',
    listPad: '0 6px',
    listItemPad: '7px 8px',
    listItemGap: '8px',
    listItemRadius: '8px',
    avatarSize: '28px',
    avatarFontSize: '11px',
    avatarDotSize: '7px',
    avatarDotBorder: '2px',
    nameFontSize: '0.8125rem',
    previewFontSize: '0.75rem',
    timeFontSize: '0.625rem',
    badgeSize: '16px',
    badgeFontSize: '0.5625rem',
    threadMaxWidth: '100%',
    threadHeaderPad: '10px 16px',
    threadHeaderAvatarSize: '28px',
    threadHeaderAvatarFontSize: '11px',
    threadHeaderNameFontSize: '0.8125rem',
    threadHeaderDescFontSize: '0.625rem',
    messagesPad: '12px 16px',
    messagesGap: '10px',
    msgAvatarSize: '24px',
    msgAvatarFontSize: '10px',
    bubbleMaxWidth: '85%',
    bubblePad: '8px 12px',
    bubbleFontSize: '0.8125rem',
    bubbleLineHeight: '1.45',
    bubbleRadius: '16px',
    bubbleRadiusTail: '4px',
    metaFontSize: '0.625rem',
    inputPad: '10px 16px',
    inputInnerPad: '8px 12px',
    inputFontSize: '0.8125rem',
    inputRadius: '22px',
    inputSendSize: '30px',
    emptyAvatarSize: '48px',
    emptyAvatarFontSize: '18px',
    emptyTitleFontSize: '1rem',
    emptyDescFontSize: '0.75rem',
  },
  /** 사이드바 전용 토큰 (PC 기본값) */
  sidebar: {
    /* ── 헤더 ── */
    headerHeight: '64px',
    headerPad: '0 12px',
    logoHeight: '28px',
    /* ── 새 대화 버튼 ── */
    newChatFontSize: '0.875rem',
    newChatPad: '10px 16px',
    newChatGap: '10px',
    /* ── 섹션 제목 (워크스페이스 / 메시지 / 채팅 히스토리) ── */
    sectionFontSize: '0.8125rem',
    sectionPad: '14px 16px 6px',
    pageSectionPad: '10px 10px 4px',
    pageGroupTitlePad: '12px 10px 6px',
    sectionListPad: '0 8px',
    /* ── 모드 아이템 (워크스페이스 목록 / DM) ── */
    itemFontSize: '0.875rem',
    itemPad: '8px 10px',
    itemGap: '10px',
    itemIconSize: '16',
    /* ── 히스토리 그룹 제목 (오늘 / 어제 등) ── */
    groupTitleFontSize: '0.6875rem',
    groupTitlePad: '16px 8px 6px',
    /* ── 히스토리 아이템 ── */
    historyFontSize: '0.875rem',
    historyPad: '8px 10px',
    historyGap: '8px',
    historyIconSize: '16',
    /* ── 하단 프로필 ── */
    footerPad: '8px 12px 12px',
    profilePad: '8px',
    profileGap: '10px',
    profileNameFontSize: '0.8125rem',
    profileEmailFontSize: '0.6875rem',
    profileAvatarSize: '32',
  },
  /** 사이드바 모바일 오버라이드 (<768px) */
  sidebarMobile: {
    headerHeight: '48px',
    headerPad: '0 10px',
    logoHeight: '24px',
    newChatFontSize: '0.8125rem',
    newChatPad: '8px 12px',
    newChatGap: '8px',
    sectionFontSize: '0.75rem',
    sectionPad: '12px 12px 4px',
    pageSectionPad: '9px 8px 4px',
    pageGroupTitlePad: '10px 8px 4px',
    sectionListPad: '0 6px',
    itemFontSize: '0.8125rem',
    itemPad: '7px 8px',
    itemGap: '8px',
    itemIconSize: '15',
    groupTitleFontSize: '0.625rem',
    groupTitlePad: '12px 6px 4px',
    historyFontSize: '0.8125rem',
    historyPad: '7px 8px',
    historyGap: '6px',
    historyIconSize: '15',
    footerPad: '6px 10px 10px',
    profilePad: '6px',
    profileGap: '8px',
    profileNameFontSize: '0.75rem',
    profileEmailFontSize: '0.625rem',
    profileAvatarSize: '28',
  },
} as const;

export const lightTheme = {
  ...shared,
  mode: 'light' as const,
  colors: {
    bgPrimary: '#ffffff',
    bgSecondary: '#f7f7f8',
    bgTertiary: '#f0f0f0',
    bgSidebar: '#f9f9f9',
    bgInput: '#f4f4f4',
    bgInputHover: '#efefef',
    bgHover: '#ececec',
    bgCode: '#f6f8fa',
    bgCodeHeader: '#e8e8e8',
    bgUserMessage: '#f7f7f8',
    bgAssistantMessage: '#ffffff',
    bgTooltip: '#0d0d0d',
    bgOverlay: 'rgba(0, 0, 0, 0.5)',

    textPrimary: '#0d0d0d',
    textSecondary: '#6e6e80',
    textMuted: '#8e8ea0',
    textInverse: '#ffffff',
    textLink: '#2b2b2b',

    border: '#e5e5e5',
    borderLight: '#f0f0f0',
    borderInput: '#d9d9e3',

    accent: '#0d0d0d',
    accentHover: '#2b2b2b',
    accentLight: 'rgba(13, 13, 13, 0.06)',

    success: '#10a37f',
    error: '#ef4444',
    errorBg: '#fef2f2',
    warning: '#f59e0b',
    warningBg: '#fffbeb',
    info: '#3b82f6',
    infoBg: '#eff6ff',

    /** 사용량 바 전용 색상 (잔여 비율 기준) */
    usageBarHealthy: '#10a37f',
    usageBarCaution: '#c9942e',
    usageBarCritical: '#ef4444',
    /** 스토리지 바 기본 (사용 비율 기준, 낮을 때) */
    usageBarNeutral: 'rgba(0,0,0,0.15)',

    scrollbar: '#c5c5d2',
    scrollbarHover: '#9e9eb1',
  },
} as const;

export const darkTheme = {
  ...shared,
  mode: 'dark' as const,
  colors: {
    bgPrimary: '#212121',
    bgSecondary: '#2f2f2f',
    bgTertiary: '#424242',
    bgSidebar: '#171717',
    bgInput: '#2f2f2f',
    bgInputHover: '#383838',
    bgHover: '#3a3a3a',
    bgCode: '#1e1e2e',
    bgCodeHeader: '#2d2d3d',
    bgUserMessage: '#2f2f2f',
    bgAssistantMessage: '#212121',
    bgTooltip: '#f7f7f8',
    bgOverlay: 'rgba(0, 0, 0, 0.7)',

    textPrimary: '#ececec',
    textSecondary: '#b4b4b4',
    textMuted: '#8e8ea0',
    textInverse: '#0d0d0d',
    textLink: '#d9d9d9',

    border: '#424242',
    borderLight: '#333333',
    borderInput: '#565869',

    accent: '#ececec',
    accentHover: '#d9d9d9',
    accentLight: 'rgba(236, 236, 236, 0.08)',

    success: '#10a37f',
    error: '#f87171',
    errorBg: '#3b1c1c',
    warning: '#fbbf24',
    warningBg: '#3b2e1c',
    info: '#60a5fa',
    infoBg: '#1c2b3b',

    /** 사용량 바 전용 색상 (잔여 비율 기준) */
    usageBarHealthy: '#10a37f',
    usageBarCaution: '#c9942e',
    usageBarCritical: '#f87171',
    /** 스토리지 바 기본 (사용 비율 기준, 낮을 때) */
    usageBarNeutral: 'rgba(255,255,255,0.2)',

    scrollbar: '#555555',
    scrollbarHover: '#777777',
  },
} as const;

// string-literal 을 string 으로 확장하는 유틸리티 타입
type WidenStrings<T> = {
  readonly [K in keyof T]: T[K] extends string ? string : T[K] extends object ? WidenStrings<T[K]> : T[K];
};

export type AppTheme = WidenStrings<Omit<typeof lightTheme, 'mode'>> & { mode: 'light' | 'dark' };

